import { useEffect, useState, type FormEvent } from "react";
import {
  createFacilityPart,
  deleteFacilityPart,
  listFacilityParts,
  updateFacilityPart,
  listFacilityVendors,
  listFacilityGroups,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { FACILITY_CATEGORIES, FACILITY_PART_UNITS } from "../lib/constants";
import type {
  CreateFacilityPartInput,
  FacilityPartRow,
  UpdateFacilityPartInput,
} from "../lib/types";
import { facilityLabel } from "./FacilitiesPage";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";

const FACILITY_PART_SORT: Record<string, SortAccessor<FacilityPartRow>> = {
  name: (p) => p.name,
  part_number: (p) => p.part_number,
  category: (p) => facilityLabel(FACILITY_CATEGORIES, p.category),
  location: (p) => p.location,
  on_hand: (p) => p.quantity_on_hand,
  unit_cost: (p) => p.unit_cost_cents,
  supplier: (p) => p.supplier,
};

/** Format integer cents as a localized USD currency string. */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** Inventory catalog — searchable parts list with category + low-stock filters,
 *  inline create/edit modal, and delete. Adding parts to work orders (which
 *  consumes stock) lives on the work-order detail page. */
export function FacilityPartsPage() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [lowStock, setLowStock] = useState(false);
  const [belowReorder, setBelowReorder] = useState(false);
  // Group is a client-side convenience filter ("" = all, "none" = unassigned).
  const [group, setGroup] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<FacilityPartRow | null>(null);

  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useApi(
    (signal) =>
      listFacilityParts(
        {
          ...(debounced ? { q: debounced } : {}),
          ...(category ? { category } : {}),
          ...(lowStock ? { lowStock: true } : {}),
          ...(belowReorder ? { belowReorder: true } : {}),
        },
        signal,
      ),
    [debounced, category, lowStock, belowReorder],
  );
  const allParts = data?.parts ?? [];
  const parts =
    group === "none"
      ? allParts.filter((p) => !p.group_id)
      : group
        ? allParts.filter((p) => p.group_id === group)
        : allParts;
  const { rows: sortedParts, sort, toggle } = useTableSort(parts, FACILITY_PART_SORT);
  const filtersActive = !!(debounced || category || lowStock || belowReorder || group);
  // Reorder-alert count across the CURRENTLY loaded set (server derives the flag).
  const reorderCount = parts.filter((p) => p.below_reorder).length;

  const delMut = useMutation(deleteFacilityPart);

  async function onDelete(part: FacilityPartRow) {
    const yes = await confirmDialog({
      title: "Delete part",
      message: `Delete "${part.name}" from the inventory catalog? This can't be undone.`,
      danger: true,
      confirmLabel: "Delete",
    });
    if (!yes) return;
    const ok = await delMut.run(part.id);
    if (ok) reload();
  }

  return (
    <>
      <PageBar
        crumbs="Parts"
        actions={
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            + New part
          </button>
        }
      />
      <div className="content">
        <div className="card">
          <div className="card-head">
            <h3>Parts &amp; inventory</h3>
            <div className="btn-row" style={{ alignItems: "center" }}>
              <input
                type="text"
                className="input-inline"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or part #…"
                aria-label="Search parts"
              />
              <select
                className="input-inline"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {FACILITY_CATEGORIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              {groups.length > 0 ? (
                <select
                  className="input-inline"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  aria-label="Filter by group"
                >
                  <option value="">All groups</option>
                  <option value="none">Unassigned</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <label className="toggle parts-lowstock-toggle">
                <input
                  type="checkbox"
                  checked={lowStock}
                  onChange={(e) => setLowStock(e.target.checked)}
                />
                <span>Low stock only</span>
              </label>
              <label className="toggle parts-lowstock-toggle">
                <input
                  type="checkbox"
                  checked={belowReorder}
                  onChange={(e) => setBelowReorder(e.target.checked)}
                />
                <span>
                  Below reorder
                  {!belowReorder && reorderCount > 0 ? (
                    <span className="badge facility-part-low" style={{ marginLeft: 6 }}>
                      {reorderCount}
                    </span>
                  ) : null}
                </span>
              </label>
              <span className="faint">{parts.length}</span>
            </div>
          </div>
          <div className="card-body">
            {delMut.error ? <Alert kind="error">{delMut.error}</Alert> : null}
            {loading ? (
              <Loading label="Loading parts…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : parts.length === 0 ? (
              <EmptyState>
                {filtersActive
                  ? "No parts match the current filters."
                  : "No parts yet. Add the first part to start tracking inventory."}
              </EmptyState>
            ) : (
              <table className="data facility-part-table">
                <thead>
                  <tr>
                    <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                    <SortTh field="part_number" sort={sort} onSort={toggle}>Part #</SortTh>
                    <SortTh field="category" sort={sort} onSort={toggle}>Category</SortTh>
                    <SortTh field="location" sort={sort} onSort={toggle}>Location</SortTh>
                    <SortTh field="on_hand" sort={sort} onSort={toggle} className="num">On hand</SortTh>
                    <SortTh field="unit_cost" sort={sort} onSort={toggle} className="num">Unit cost</SortTh>
                    <SortTh field="supplier" sort={sort} onSort={toggle}>Supplier</SortTh>
                    {groups.length > 0 ? <th>Group</th> : null}
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedParts.map((p) => (
                    <FacilityPartTr
                      key={p.id}
                      part={p}
                      groupName={
                        groups.length > 0
                          ? p.group_id
                            ? groups.find((g) => g.id === p.group_id)?.name ?? p.group_id
                            : "—"
                          : null
                      }
                      onEdit={() => setEditing(p)}
                      onDelete={() => onDelete(p)}
                      deleting={delMut.pending}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showCreate ? (
        <FacilityPartModal
          part={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            reload();
          }}
        />
      ) : null}

      {editing ? (
        <FacilityPartModal
          part={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function FacilityPartTr({
  part,
  groupName,
  onEdit,
  onDelete,
  deleting,
}: {
  part: FacilityPartRow;
  /** Resolved group name for the Group column; null hides the column (no groups exist). */
  groupName: string | null;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <tr
      className="clickable"
      onClick={onEdit}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onEdit();
      }}
    >
      <td>
        <strong>{part.name}</strong>
      </td>
      <td className="muted mono">{part.part_number || "—"}</td>
      <td className="muted">{facilityLabel(FACILITY_CATEGORIES, part.category)}</td>
      <td className="muted">{part.location || "—"}</td>
      <td className="num">
        {part.quantity_on_hand}
        {part.below_reorder ? (
          <span className="badge facility-part-low" title="At or below the reorder point">
            Reorder
          </span>
        ) : part.low_stock ? (
          <span className="badge facility-part-low" title="At or below minimum">
            Low
          </span>
        ) : null}
      </td>
      <td className="num muted">{formatCents(part.unit_cost_cents)}</td>
      <td className="muted">{part.supplier || "—"}</td>
      {groupName !== null ? <td className="muted">{groupName}</td> : null}
      <td className="row-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn small" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn small danger"
          disabled={deleting}
          onClick={onDelete}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Add/edit part modal — null part = create. Unit cost is entered in dollars and
 *  converted to integer cents on save (the API stores `unit_cost_cents`). */
export function FacilityPartModal({
  part,
  onClose,
  onSaved,
}: {
  part: FacilityPartRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(part?.name ?? "");
  const [partNumber, setPartNumber] = useState(part?.part_number ?? "");
  const [category, setCategory] = useState(part?.category ?? "");
  const [location, setLocation] = useState(part?.location ?? "");
  const [quantity, setQuantity] = useState(String(part?.quantity_on_hand ?? 0));
  const [minQuantity, setMinQuantity] = useState(String(part?.min_quantity ?? 0));
  const [reorderPoint, setReorderPoint] = useState(String(part?.reorder_point ?? 0));
  const [unit, setUnit] = useState(part?.unit ?? "each");
  // Unit cost is held as a dollars string; convert to cents on save.
  const [unitCost, setUnitCost] = useState(
    part?.unit_cost_cents != null ? (part.unit_cost_cents / 100).toFixed(2) : "",
  );
  const [supplier, setSupplier] = useState(part?.supplier ?? "");
  const [vendorId, setVendorId] = useState(part?.vendor_id ?? "");
  const [groupId, setGroupId] = useState(part?.group_id ?? "");
  const [notes, setNotes] = useState(part?.notes ?? "");

  // Vendor registry for the structured "Supplier" picker (0085).
  const vendorsApi = useApi((s) => listFacilityVendors(s), []);
  const vendors = vendorsApi.data?.vendors ?? [];
  const groupsApi = useApi((s) => listFacilityGroups(s), []);
  const groups = groupsApi.data?.groups ?? [];

  const createMut = useMutation(createFacilityPart);
  const updateMut = useMutation((patch: UpdateFacilityPartInput) =>
    updateFacilityPart(part!.id, patch),
  );
  const pending = createMut.pending || updateMut.pending;
  const error = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Dollars → integer cents; blank leaves it unset (null).
    const dollars = unitCost.trim();
    const cents = dollars === "" ? null : Math.round(Number(dollars) * 100);
    if (cents != null && Number.isNaN(cents)) return;

    const body: CreateFacilityPartInput = {
      name: name.trim(),
      part_number: partNumber.trim() || null,
      category: category || null,
      location: location.trim() || null,
      quantity_on_hand: Math.max(0, Math.round(Number(quantity) || 0)),
      min_quantity: Math.max(0, Math.round(Number(minQuantity) || 0)),
      reorder_point: Math.max(0, Math.round(Number(reorderPoint) || 0)),
      unit: unit || null,
      unit_cost_cents: cents,
      supplier: supplier.trim() || null,
      vendor_id: vendorId || null,
      group_id: groupId || null,
      notes: richTextIsEmpty(notes) ? null : notes,
    };

    const res = part ? await updateMut.run(body) : await createMut.run(body);
    if (res) onSaved();
  }

  return (
    <Modal
      title={part ? "Edit part" : "Add part"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="facility-part-form"
            disabled={pending || !name.trim()}
          >
            {pending ? <Spinner label="Saving…" /> : part ? "Save changes" : "Add part"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="facility-part-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 16x25x1 air filter"
            autoFocus
            required
          />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Part #</span>
            <input
              type="text"
              value={partNumber}
              onChange={(e) => setPartNumber(e.target.value)}
              placeholder="MFR-0142"
            />
          </label>
          <label className="field">
            <span className="label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">—</option>
              {FACILITY_CATEGORIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="label">Location</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Stockroom A, shelf 3"
          />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Quantity on hand</span>
            <input
              type="number"
              min={0}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Minimum quantity</span>
            <input
              type="number"
              min={0}
              step={1}
              value={minQuantity}
              onChange={(e) => setMinQuantity(e.target.value)}
            />
            <span className="hint">At or below this, the part shows a Low badge.</span>
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Reorder point</span>
            <input
              type="number"
              min={0}
              step={1}
              value={reorderPoint}
              onChange={(e) => setReorderPoint(e.target.value)}
            />
            <span className="hint">
              When on-hand drops to/below this (and it is above 0), facilities
              staff get a low-stock alert. Leave at 0 to disable alerts.
            </span>
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Unit</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value)}>
              {FACILITY_PART_UNITS.map((u) => (
                <option key={u.code} value={u.code}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Unit cost (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder="0.00"
            />
          </label>
        </div>
        <label className="field">
          <span className="label">Supplier (vendor)</span>
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">—</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          <span className="hint">Pick a vendor from Settings → Facilities → Vendors. Use the free-text field below for an ad-hoc supplier.</span>
        </label>
        <label className="field">
          <span className="label">Supplier (free text)</span>
          <input
            type="text"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            placeholder="e.g. Grainger"
          />
        </label>
        {groups.length > 0 || part?.group_id ? (
          <label className="field">
            <span className="label">Group <span className="faint">(controls who can see this part)</span></span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={groupsApi.loading}>
              <option value="">Unassigned (everyone with Facilities access)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              {groupId && !groups.some((g) => g.id === groupId) ? (
                <option value={groupId}>{groupId}</option>
              ) : null}
            </select>
          </label>
        ) : null}
        <label className="field">
          <span className="label">Notes</span>
          <RichTextEditor value={notes} onChange={setNotes} placeholder="Notes…" />
        </label>
      </form>
    </Modal>
  );
}
